import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { auditoriaService } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { FiFilter, FiRefreshCw, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const ACCIONES = [
  { value: '', label: 'Todas' },
  { value: 'INSERT', label: 'Inserción' },
  { value: 'UPDATE', label: 'Actualización' },
  { value: 'DELETE', label: 'Eliminación' },
];

const TABLAS = [
  '',
  'auth',
  'usuarios',
  'dispositivos',
  'comandos',
  'codigos',
];

const Auditoria = () => {
  const [filtros, setFiltros] = useState({
    nombre_usuario: '',
    fecha_desde: '',
    fecha_hasta: '',
    accion: '',
    tabla_recurso: '',
    ip: '',
  });
  const [pagina, setPagina] = useState(1);
  const [limite] = useState(25);
  const [mostrarFiltros, setMostrarFiltros] = useState(true);

  const params = {
    pagina,
    limite,
    ...filtros,
  };
  if (params.fecha_desde) params.fecha_desde = params.fecha_desde.replace('T', ' ') + ':00';
  if (params.fecha_hasta) params.fecha_hasta = params.fecha_hasta.replace('T', ' ') + ':00';
  Object.keys(params).forEach((key) => {
    if (params[key] === '' || params[key] == null) delete params[key];
  });

  const { data, isLoading, error, refetch } = useQuery(
    ['auditoria', params],
    async () => {
      const res = await auditoriaService.listar(params);
      return res.data;
    },
    { keepPreviousData: true }
  );

  const registros = data?.datos ?? [];
  const paginacion = data?.paginacion ?? { total: 0, pagina: 1, total_paginas: 1 };

  const handleFiltro = (campo, valor) => {
    setFiltros((prev) => ({ ...prev, [campo]: valor }));
    setPagina(1);
  };

  const limpiarFiltros = () => {
    setFiltros({
      nombre_usuario: '',
      fecha_desde: '',
      fecha_hasta: '',
      accion: '',
      tabla_recurso: '',
      ip: '',
    });
    setPagina(1);
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return '—';
    const d = new Date(fecha);
    return d.toLocaleString('es-ES', {
      dateStyle: 'short',
      timeStyle: 'medium',
    });
  };

  const badgeAccion = (accion) => {
    const estilos = {
      INSERT: 'bg-green-100 text-green-800',
      UPDATE: 'bg-blue-100 text-blue-800',
      DELETE: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${estilos[accion] || 'bg-gray-100 text-gray-800'}`}>
        {accion}
      </span>
    );
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 font-medium">Error al cargar la auditoría</p>
          <p className="text-sm text-red-600 mt-1">
            {error.response?.data?.mensaje || error.response?.data?.error || error.message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Auditoría</h1>
          <p className="text-gray-600">Registro de acciones: usuario, fecha, IP, inserciones, actualizaciones y eliminaciones</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setMostrarFiltros((v) => !v)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <FiFilter className="mr-2" />
            {mostrarFiltros ? 'Ocultar filtros' : 'Mostrar filtros'}
          </button>
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <FiRefreshCw className="mr-2" />
            Actualizar
          </button>
        </div>
      </div>

      {mostrarFiltros && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h2 className="text-sm font-medium text-gray-700 mb-3">Filtros</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Usuario</label>
              <input
                type="text"
                value={filtros.nombre_usuario}
                onChange={(e) => handleFiltro('nombre_usuario', e.target.value)}
                placeholder="Nombre o API/Sistema"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Fecha desde</label>
              <input
                type="datetime-local"
                value={filtros.fecha_desde}
                onChange={(e) => handleFiltro('fecha_desde', e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Fecha hasta</label>
              <input
                type="datetime-local"
                value={filtros.fecha_hasta}
                onChange={(e) => handleFiltro('fecha_hasta', e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Acción</label>
              <select
                value={filtros.accion}
                onChange={(e) => handleFiltro('accion', e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                {ACCIONES.map((o) => (
                  <option key={o.value || 'all'} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Recurso / Tabla</label>
              <select
                value={filtros.tabla_recurso}
                onChange={(e) => handleFiltro('tabla_recurso', e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">Todas</option>
                {TABLAS.filter(Boolean).map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">IP</label>
              <input
                type="text"
                value={filtros.ip}
                onChange={(e) => handleFiltro('ip', e.target.value)}
                placeholder="Ej. 192.168."
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="mt-3">
            <button
              type="button"
              onClick={limpiarFiltros}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Limpiar filtros
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Total: <span className="font-medium text-gray-900">{paginacion.total}</span> registros
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : registros.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No hay registros de auditoría con los filtros aplicados.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha / Hora</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acción</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recurso</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID recurso</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Detalles</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {registros.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                      {formatearFecha(r.fecha_hora)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="font-medium text-gray-900">{r.nombre_usuario ?? '—'}</span>
                      {r.usuario_id != null && (
                        <span className="text-gray-500 ml-1">(ID: {r.usuario_id})</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{badgeAccion(r.accion)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{r.tabla_recurso}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{r.recurso_id ?? '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={r.detalles ?? ''}>
                      {r.detalles ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 font-mono">{r.ip ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {paginacion.total_paginas > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Página {paginacion.pagina} de {paginacion.total_paginas}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPagina((p) => Math.max(1, p - 1))}
                disabled={paginacion.pagina <= 1}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <FiChevronLeft className="mr-1" />
                Anterior
              </button>
              <button
                type="button"
                onClick={() => setPagina((p) => Math.min(paginacion.total_paginas, p + 1))}
                disabled={paginacion.pagina >= paginacion.total_paginas}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Siguiente
                <FiChevronRight className="ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Auditoria;
